const FULL_ACCESS_ROLES = ['owner', 'admin'];
const BRANCH_ADMIN_ROLES = ['branch_admin'];
const RESTAURANT_SCOPED_ROLES = ['restaurant_manager', 'manager'];
const BRANCH_SCOPED_ROLES = ['branch_manager', 'waiter', 'chef'];

export function hasFullAccess(user) {
  return user && FULL_ACCESS_ROLES.includes(user.role);
}

export function isBranchAdmin(user) {
  return user?.role === 'branch_admin';
}

/** Branch admin: owner-level access but scoped to assigned branches only */
export function hasBranchAdminAccess(user) {
  return hasFullAccess(user) || isBranchAdmin(user);
}

/** Collect assigned branch ids from any of the common fields */
export function getBranchAdminBranchIds(user) {
  if (!user) return [];
  const seen = new Set();
  const result = [];
  const push = (id) => {
    if (id == null || id === '') return;
    const s = String(id);
    if (seen.has(s)) return;
    seen.add(s);
    result.push(id);
  };
  for (const list of [user.branch_ids, user.branchIds, user.assigned_branch_ids, user.assignedBranchIds]) {
    if (Array.isArray(list)) list.forEach(push);
  }
  push(user.branch_id);
  push(user.branchId);
  return result;
}

export function canAccessRestaurant(user, restaurantId) {
  if (!user || !restaurantId) return false;
  if (hasFullAccess(user)) return true;
  if (RESTAURANT_SCOPED_ROLES.includes(user.role) && user.restaurantId === restaurantId) return true;
  return false;
}

export function canAccessBranch(user, branchId) {
  if (!user || !branchId) return false;
  if (hasFullAccess(user)) return true;
  if (isBranchAdmin(user)) return getBranchAdminBranchIds(user).map(String).includes(String(branchId));
  if (BRANCH_SCOPED_ROLES.includes(user.role) && user.branchId === branchId) return true;
  if (RESTAURANT_SCOPED_ROLES.includes(user.role) && user.restaurantId) return true;
  return false;
}

export function canCreateUsers(user) {
  if (!user) return false;
  if (hasFullAccess(user)) return true;
  if (isBranchAdmin(user)) return true;
  return user.permissions?.manageUsers === true;
}

export function isChef(user) {
  return user?.role === 'chef';
}

export function isWaiter(user) {
  return user?.role === 'waiter';
}

export function isRestaurantManager(user) {
  return user && (user.role === 'restaurant_manager' || user.role === 'manager');
}

export function isBranchManager(user) {
  return user?.role === 'branch_manager';
}

export function canAccessManageRestaurant(user) {
  if (!user) return false;
  return hasFullAccess(user) || isRestaurantManager(user);
}

export function canAccessBranches(user) {
  if (!user) return false;
  return hasFullAccess(user) || isBranchAdmin(user) || isRestaurantManager(user) || isBranchManager(user);
}

export function canAccessPOS(user) {
  if (!user) return false;
  if (hasFullAccess(user)) return true;
  if (user.role === 'chef') return true;
  return user.permissions?.accessPOS === true;
}

export function canAccessQRMenu(user) {
  if (!user) return false;
  return hasFullAccess(user) || isBranchAdmin(user) || user.permissions?.manageRestaurants || isRestaurantManager(user);
}

const ALL_ROLES = ['owner', 'admin', 'branch_admin', 'restaurant_manager', 'branch_manager', 'waiter', 'chef', 'manager'];

export function getAssignableRoles(user) {
  if (!user) return [];
  if (hasFullAccess(user)) return ALL_ROLES;
  // Branch admin can assign roles within their branches (but not owner/admin/branch_admin)
  if (isBranchAdmin(user)) return ['branch_manager', 'waiter', 'chef', 'manager'];
  if (isRestaurantManager(user)) return ['restaurant_manager', 'branch_manager', 'waiter', 'chef', 'manager'];
  if (isBranchManager(user)) return ['branch_manager', 'waiter', 'chef'];
  return [];
}
